import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Inject, Input, OnDestroy, OnInit, Output, TemplateRef, ViewChild } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { SubSink } from "subsink";
import { AuthService } from "../../../../../../auth/auth.service";
import { BASE_API_URL } from "../../../../../../core/environment.tokens";
import { MessagingService } from "../../../../../../core/messaging/messaging.service";
import { SeverityType } from "../../../../../../core/messaging/severity-type.enum";
import { Textbox } from "../../../../../../dynamic-forms/inputs/textbox/textbox.model";
import { ChaseIdComponent } from "../../../../../../shared/chase-grid/chase-id.component";
import { BulkAction } from "../../../../../../shared/grid/bulk-actions/bulk-action.model";
import { GridView } from "../../../../../../shared/grid/grid-menu/grid-views/grid-view.model";
import { GridViewsState } from "../../../../../../shared/grid/grid-menu/grid-views/grid-views-state.model";
import { GridViewsService } from "../../../../../../shared/grid/grid-menu/grid-views/grid-views.service";
import { GridPipeName } from "../../../../../../shared/grid/grid-pipe.enum";
import { GridColumnDefinition } from "../../../../../../shared/grid/models/grid-column-definition.model";
import { GridConfiguration } from "../../../../../../shared/grid/models/grid-configuration.model";
import { GridFilter } from "../../../../../../shared/grid/models/grid-filter.model";
import { GridRequest } from "../../../../../../shared/grid/models/grid-request.model";
import { ServerGridComponent } from "../../../../../../shared/grid/server-grid/server-grid.component";
import { ArrayHelper } from "../../../../../../utilities/contracts/array-helper";
import { DateFormats } from "../../../../../../utilities/contracts/helper-types";
import { ChaseAssignService } from "../../../../../api/unassign-chase/chase-assign.service";
import { RetrievalDocumentServiceService } from "../../../../retrieval/retrieval-document-service.service";
import { MEMBER_MEMBER_QUERY_GRID } from "../../../chase-detail/chase-detail-chart/attributes";
import { MemberDetailService } from "../../member-detail.service";

@Component({
  selector: "member-member-detail-body-grids-chases",
  templateUrl: "./member-detail-body-grids-chases.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MemberDetailBodyGridsChasesComponent implements OnInit, OnDestroy {
  private sink = new SubSink();
  @ViewChild("chaseIdColumn", { static: true }) chaseIdColumn: TemplateRef<ChaseIdComponent>;
  @ViewChild(ServerGridComponent, { static: true }) serverGridComponent: ServerGridComponent;
  @Output() formClose = new EventEmitter();
  @Input() clinical: boolean | null;
  private readonly possibleDataSetTypes = ["Archived", "Inactive"];
  masterMemberId: string;
  dataSet: string;
  configuration: GridConfiguration;
  data: any[] = [];
  selection: any[];
  isPendModalVisible = false;
  isAssignToChaseModalVisible = false;
  isCopyChartModelVisible = false;
  selectedChases: any[];
  request: GridRequest;
  actions: BulkAction[];
  refreshGrid = new EventEmitter<GridConfiguration>(true);
  refreshViews = new EventEmitter<GridView>(true);
  views: GridViewsState;
  selectedChasesStatus: any[];
  hasClinicalNonClinicalChases = false;
  showMemberWithChases: string;
  selectedChaseIdsForAssignment: number[];
  customerMemberId: string;
  useCustomerMemberId = false;

  constructor(
    @Inject(BASE_API_URL) private readonly baseApiUrl: string,
    private route: ActivatedRoute,
    private messagingService: MessagingService,
    private authService: AuthService,
    private readonly chaseAssignService: ChaseAssignService,
    private readonly retrievalDocumentService: RetrievalDocumentServiceService,
    private changeDetector: ChangeDetectorRef,
    private gridViewsService: GridViewsService,
    private memberDetailService: MemberDetailService
  ) { }

  ngOnInit() {
    this.sink.add(
      this.refreshViews.subscribe(gridView => this.getViews(gridView)),
      this.route.data.subscribe(data => {
        if (data && data.useCustomerMemberId !== undefined) {
          this.useCustomerMemberId = data.useCustomerMemberId;
        }
      }),
      this.route.paramMap.subscribe(params => {
        this.masterMemberId = params.get("masterMemberId") ?? "";
        this.dataSet = "Unarchived";
        if (this.useCustomerMemberId) {
          this.customerMemberId = params.get("memberId") ?? "";
        }
      }),
      this.memberDetailService.showMemberWithChasesFlag.subscribe(data => {
        this.showMemberWithChases = data;
        this.createGrid();
        this.changeDetector.markForCheck();
      })
    );
  }

  ngOnDestroy() {
    this.sink.unsubscribe();
  }

  getRouteUrl(dataSet: string): string {
    return this.possibleDataSetTypes.includes(dataSet)
      ? "/members/archive/:chaseId"
      : "/members/chase/:chaseId";
  }

  createGrid(): void {
    this.updateGridConfiguration();

    this.request = new GridRequest({
      url: `${this.baseApiUrl}chase/query-archive`,
      filters: [
        new GridFilter({
          input: new Textbox(),
          key: this.useCustomerMemberId
            ? "MemberSourceAliasId"
            : "MasterMemberId",
          value: this.useCustomerMemberId
            ? this.customerMemberId
            : this.masterMemberId,
          show: false,
        }),
        new GridFilter({
          input: new Textbox(),
          key: "DataSet",
          value: this.dataSet,
          show: false,
        }),
        new GridFilter({
          input: new Textbox(),
          key: "MemberWithCharts",
          value: this.showMemberWithChases,
          show: false,
        }),
      ],
    });

    this.getViews();
    this.actions = this.getActions();
  }

  updateGridConfiguration() {
    this.configuration = new GridConfiguration({
      columns: this.getColumns(),
      selectionMode: "multiple",
      pageSize: 25,
      pageSizeOptions: [10, 25, 50, 100],
      showActionColumn: true,
      stateName: MEMBER_MEMBER_QUERY_GRID.attributeCode,
      showViews: true,
      viewAttributeId: MEMBER_MEMBER_QUERY_GRID.attributeId,
      isStateDisabled: true,
    });
  }

  private getColumns(): GridColumnDefinition[] {
    return [
      new GridColumnDefinition({
        field: "chaseId",
        template: this.chaseIdColumn,
        header: "Chase ID",
      }),
      new GridColumnDefinition({
        field: "chaseSourceAliasId",
        header: "Customer Chase ID",
      }),
      new GridColumnDefinition({ field: "memberId", header: "Member ID" }),
      new GridColumnDefinition({
        field: "memberSourceAliasId",
        header: "Member Key",
      }),
      new GridColumnDefinition({ field: "projectName", header: "Project" }),
      new GridColumnDefinition({ field: "dataSet", header: "Project Status" }),
      new GridColumnDefinition({ field: "measureCode", header: "Measure" }),
      new GridColumnDefinition({
        field: "chaseCreateDate",
        header: "Created",
        pipeName: GridPipeName.Date,
        format: DateFormats.GRID_DATE_FORMAT,
      }),
      new GridColumnDefinition({
        field: "reportingStatusName",
        header: "Chase Status",
      }),
      new GridColumnDefinition({
        field: "serviceProviderName",
        header: "Provider",
      }),
      new GridColumnDefinition({
        field: "documentSourceLocation",
        header: "Location",
      }),
      new GridColumnDefinition({
        field: "chaseComplianceCode",
        header: "Chase Compliance",
      }),
      new GridColumnDefinition({
        field: "sampleComplianceCodeValue",
        header: "Sample Compliance",
      }),
      new GridColumnDefinition({
        field: "masterDocumentSourceId",
        header: "AID",
      }),
      new GridColumnDefinition({ field: "tagsText", header: "Tags" }),
      new GridColumnDefinition({ field: "assignedTo", header: "Assigned To" }),
      new GridColumnDefinition({
        field: "chaseDocumentPageCount",
        header: "Pages",
      }),
      new GridColumnDefinition({
        field: "chartAcquired",
        header: "Chart First Acquired",
        pipeName: GridPipeName.Date,
        format: DateFormats.GRID_DATE_FORMAT,
      }),
      new GridColumnDefinition({ field: "pendCode", header: "Pend Code" }),
      new GridColumnDefinition({ field: "pendStatus", header: "Pend Status" }),
    ];
  }

  private getActions(): BulkAction[] {
    return [
      new BulkAction({
        name: "Export Chase(s)",
        action: this.exportChases,
        alwaysEnabled: true,
      }),
      new BulkAction({
        name: "Pend Chase(s)",
        action: this.openPendModal,
      }),
      new BulkAction({
        name: "Assign Chase(s)",
        action: this.openAssignToChaseModal,
        disabled: !this.authService.user.isUserMangerAdminLead,
      }),
      new BulkAction({
        name: "Unassign Chase(s)",
        action: this.unassignToChases,
        disabled: !this.authService.user.isUserMangerAdminLead,
      }),
      new BulkAction({
        name: "Download Chart",
        action: this.submitChartDownloadRequest,
      }),
      new BulkAction({
        name: "Copy Chart",
        action: this.openCopyChartModel,
      }),
    ];
  }

  onChaseLoaded(event: any): void {
    this.changeDetector.markForCheck();
    this.serverGridComponent.data.forEach(c => {
      if (c.dataSet.toLowerCase() === "inactive") {
        c.disabled = false;
      }
    });
  }

  onMemberGridSelectionChange(event: any): void {
    const checkForArchiveOrInactiveProjects = event.findIndex(c => this.possibleDataSetTypes.includes(c.dataSet));

    if (checkForArchiveOrInactiveProjects !== -1) {
      this.actions.find(x => x.name === "Pend Chase(s)").disabled = true;
      this.actions.find(x => x.name === "Assign Chase(s)").disabled = true;
      this.actions.find(x => x.name === "Unassign Chase(s)").disabled = true;
    } else {
      this.actions.find(x => x.name === "Pend Chase(s)").disabled = false;
      this.actions.find(x => x.name === "Assign Chase(s)").disabled = !this.authService.user.isUserMangerAdminLead;
      this.actions.find(x => x.name === "Unassign Chase(s)").disabled = !this.authService.user.isUserMangerAdminLead;
    }

    this.actions = [...this.actions];
  }

  projectInActiveOrArchive(rowData: any): boolean {
    return (this.possibleDataSetTypes.includes(rowData.dataSet));
  }

  exportChases = (rowData: any): void => {
    this.setSelectedChases(rowData);
    this.serverGridComponent.exportGridData();
  }

  openPendModal = (rowData: any): void => {
    this.setSelectedChases(rowData);
    this.checkChasesStatus();
    if (ArrayHelper.isAvailable(this.selectedChases)) {
      this.isPendModalVisible = true;
    } else {
      this.messagingService.showToast("Chase(s) not in right status to be actioned upon.", SeverityType.WARN);
    }
  }

  openAssignToChaseModal = (rowData: any): void => {
    this.setSelectedChases(rowData);

    if (ArrayHelper.isAvailable(this.selectedChases)) {
      this.isAssignToChaseModalVisible = true;
    } else {
      this.messagingService.showToast("Chase(s) not in right status to be actioned upon.", SeverityType.WARN);
    }
  }

  unassignToChases = (rowData: any): void => {
    this.setSelectedChases(rowData);

    if (ArrayHelper.isAvailable(this.selectedChases)) {
      const chaseStatus = this.selectedChases.map(item => item.workflowStatusName)
      .filter((value, index, self) => self.indexOf(value) === index);
      if (chaseStatus.length > 1) {
      this.messagingService.showToast("Chases for this assignment may not be at different workflow statuses", SeverityType.ERROR);
      this.selectedChases = [];
    } else {
      const unassignedUserId = 0;
      this.chaseAssignService.assignChasesToUser(this.chaseAssignService.getSelectedChaseIds(this.selectedChases), unassignedUserId, null)
        .subscribe(() => {
          this.messagingService.showToast("Unassigned Successfully.", SeverityType.SUCCESS);
          this.closeModal();
          this.refreshGrid.emit();
        });
    }
    } else {
      this.messagingService.showToast("Chase(s) not in right status to be actioned upon.", SeverityType.WARN);
    }
  }

  submitChartDownloadRequest = (rowData: any): void => {
    const rowchase = ArrayHelper.isAvailable(rowData) ? rowData : [rowData];
    rowchase.forEach((data: any) => {
      this.downloadSelectedChaseChart(data);
    });
  }
  private downloadSelectedChaseChart(rowData: any): void {
    if (rowData.chaseDocumentPageCount > 0) {
      this.retrievalDocumentService.submitChartDownloadRequest(rowData.chaseId)
        .subscribe((result: boolean) => {
          if (result) {
            this.messagingService.showToast("Your request is being processed and you will be alerted when the file is ready for download", SeverityType.SUCCESS);
          } else {
            this.messagingService.showMessage("Your Chart download request has failed.", SeverityType.ERROR);
          }
        });
    } else {
      this.messagingService.showToast("Chart not available for download.", SeverityType.ERROR);
    }
  }

  openCopyChartModel = (rowData: any): void => {
    this.setSelectedChases(rowData);
    this.isCopyChartModelVisible = true;
  }

  closeModal(): void {
    this.selectedChases = [];
    this.selection = [];
  }

  private setSelectedChases(rowData: any): void {
    const rowChases = ArrayHelper.isAvailable(rowData) ? rowData : [rowData];
    this.selectedChases = rowChases.filter(x => !x.disabled);
    this.selectedChaseIdsForAssignment = this.chaseAssignService.getSelectedChaseIds(this.selectedChases);
  }

  private getViews(gridView: GridView | null = null): void {
    if (this.configuration.showViews) {
      this.gridViewsService.get(this.configuration.viewAttributeId).subscribe(views => {
        this.views = views;

        if (gridView != null) {
            setTimeout(() => this.serverGridComponent.onViewSelect.emit(gridView));
        }

        this.changeDetector.markForCheck();
      });
    }
  }

  checkChasesStatus(): void {
    this.selectedChasesStatus = this.selectedChases.map(item => item.workflowStatusId)
      .filter((value, index, self) => self.indexOf(value) === index);

    if (this.selectedChasesStatus.length > 1 && (this.selectedChasesStatus.indexOf(2) > -1 || this.selectedChasesStatus.indexOf(3) > -1)) {
      this.hasClinicalNonClinicalChases = true;
    } else if (this.selectedChasesStatus.length === 1 && (this.selectedChasesStatus.indexOf(2) > -1 || this.selectedChasesStatus.indexOf(3) > -1)) {
      this.hasClinicalNonClinicalChases = false;
      this.clinical = false;
    } else {
      this.clinical = true;
    }
  }
}
